import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import type { ToastActionConfig } from "@/hooks/use-toast";

import { cn } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-0 left-1/2 -translate-x-1/2 z-[2147483000] flex max-h-screen w-full flex-col gap-3 p-4 pt-[max(1.5rem,env(safe-area-inset-top))] sm:pt-8 md:max-w-[520px] pointer-events-none [&>*]:pointer-events-auto",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-4 overflow-hidden rounded-2xl p-5 pr-12 shadow-2xl transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-full data-[state=open]:duration-300",
  {
    variants: {
      variant: {
        default: "border-2 border-border bg-card text-foreground before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-primary before:rounded-l-2xl",
        success: "border-2 border-success/60 bg-card text-foreground before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-success before:rounded-l-2xl",
        destructive: "border-2 border-destructive/60 bg-card text-foreground before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-destructive before:rounded-l-2xl",
        warning: "border-2 border-warning/60 bg-card text-foreground before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-warning before:rounded-l-2xl",
        info: "border-2 border-info/60 bg-card text-foreground before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-info before:rounded-l-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);


const toastIconMap = {
  default: Info,
  success: CheckCircle2,
  destructive: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const toastIconColorMap = {
  default: "text-primary",
  success: "text-success",
  destructive: "text-destructive",
  warning: "text-warning",
  info: "text-info",
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & 
    VariantProps<typeof toastVariants> & 
    { showIcon?: boolean; actions?: ToastActionConfig[] }
>(({ className, variant = "default", showIcon = true, actions, children, ...props }, ref) => {
  const Icon = toastIconMap[variant || "default"];
  const iconColor = toastIconColorMap[variant || "default"];

  return (
    <ToastPrimitives.Root 
      ref={ref} 
      className={cn(toastVariants({ variant }), className)} 
      {...props}
    >
      {showIcon && (
        <div className={cn("flex-shrink-0 mt-0.5", iconColor)}>
          <Icon className="h-5 w-5" strokeWidth={2.5} />
        </div>
      )}
      <div className="flex-1 space-y-2.5">
        <div className="space-y-1">
          {children}
        </div>
        {actions && actions.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {actions.map((actionConfig, index) => (
              <ToastPrimitives.Close key={index} asChild>
                <button
                  onClick={actionConfig.onClick}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                    {
                      "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm": actionConfig.variant === "primary",
                      "bg-secondary text-secondary-foreground hover:bg-secondary/80": actionConfig.variant === "secondary",
                      "bg-background/80 text-foreground border border-border/50 hover:bg-background hover:border-border": actionConfig.variant === "default" || !actionConfig.variant,
                      "text-foreground/70 hover:text-foreground hover:bg-muted/50": actionConfig.variant === "ghost",
                    }
                  )}
                >
                  {actionConfig.icon && <span className="flex-shrink-0">{actionConfig.icon}</span>}
                  {actionConfig.label}
                </button>
              </ToastPrimitives.Close>
            ))}
          </div>
        )}
      </div>
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-background/80 px-4 text-sm font-medium ring-offset-background transition-all hover:bg-background hover:border-border hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-3 top-3 rounded-full p-1.5 text-foreground/40 opacity-70 transition-all hover:opacity-100 hover:bg-muted/60 hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title 
    ref={ref} 
    className={cn("text-[15px] font-semibold leading-tight tracking-tight", className)} 
    {...props} 
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description 
    ref={ref} 
    className={cn("text-sm leading-relaxed text-foreground/70", className)} 
    {...props} 
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
